import { Link } from "react-router-dom";
import { ArrowRight, Check, CreditCard, Landmark, Wallet, Shuffle, Globe, Coins } from "lucide-react";
import { Container, SectionHead } from "@/components/common/Section";
import { Reveal } from "@/lib/anim";
import { PLATFORMS } from "@/lib/content";
import EcosystemDiagram from "@/components/visuals/EcosystemDiagram";
import DashboardMock from "@/components/visuals/DashboardMock";

/* ---------- Section 12: Payment Infrastructure ---------- */

export function InfrastructureSection() {
  return (
    <section data-testid="infrastructure-section" className="py-20 lg:py-28">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB]">
              <span className="h-px w-6 bg-[#2563EB]/50" aria-hidden="true" />
              Payments Infrastructure
            </span>
            <h2 className="mt-4 text-[32px] font-semibold leading-[1.12] tracking-tight text-[#1D305B] sm:text-4xl lg:text-[44px]">
              Technology that connects the pieces of <span className="text-brand-gradient">modern finance.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#64748B] sm:text-lg">
              Payments are no longer a single transaction between two endpoints. They span banks, payment gateways,
              wallets, cards, currencies, compliance systems and financial partners.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#64748B] sm:text-lg">
              iJeeva brings these moving parts together through configurable, API-driven platforms designed for scale,
              integration and operational control.
            </p>
          </Reveal>
          <div className="lg:col-span-7">
            <EcosystemDiagram />
          </div>
        </div>
      </Container>
    </section>
  );
}

/* ---------- Section 13: Platform Portfolio ---------- */

function MiniZen() {
  const bars = [40, 58, 46, 70, 62, 84, 76, 95];
  return (
    <div className="h-full overflow-hidden rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded bg-[#1D305B] text-[9px] font-bold text-white">Z</span>
          <span className="text-[11px] font-semibold text-[#1D305B]">ZenPay · Overview</span>
        </span>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-600">98.7% success</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-[#F8FAFC] p-2.5">
          <p className="text-[9px] text-[#94A3B8]">Volume today</p>
          <p className="text-[13px] font-bold text-[#1D305B]">₹48.2L</p>
        </div>
        <div className="rounded-lg bg-[#F8FAFC] p-2.5">
          <p className="text-[9px] text-[#94A3B8]">Merchants</p>
          <p className="text-[13px] font-bold text-[#1D305B]">1,284</p>
        </div>
      </div>
      <div className="mt-2.5 flex h-16 items-end gap-1 rounded-lg bg-[#F8FAFC] p-2">
        {bars.map((h, i) => (
          <div key={i} className={`flex-1 rounded-sm ${i % 3 === 2 ? "bg-[#0EA5A4]/70" : "bg-[#2563EB]/80"}`} style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

function MiniEmulation() {
  return (
    <div className="h-full overflow-hidden rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-1">
        {["Bank", "Automation", "Flow"].map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-1">
            <span
              className={`flex-1 rounded-lg border px-2 py-2 text-center text-[10px] font-semibold ${
                i === 1 ? "border-[#2563EB]/30 bg-[#2563EB]/[0.06] text-[#2563EB]" : "border-[#E2E8F0] bg-[#F8FAFC] text-[#334155]"
              }`}
            >
              {s}
            </span>
            {i < 2 && <span className="h-px w-2 bg-[#CBD5E1]" />}
          </div>
        ))}
      </div>
      <div className="font-mono-brand mt-3 space-y-1.5 rounded-lg bg-[#1D305B] p-3 text-[9.5px] leading-relaxed text-white/70">
        <p><span className="text-amber-400">[DETECT]</span> statement format drift</p>
        <p><span className="text-teal-300">[HEAL]</span> parser regenerated · checks passed</p>
        <p><span className="text-emerald-400">[DEPLOY]</span> promoted to production</p>
      </div>
    </div>
  );
}

function MiniWallet() {
  return (
    <div className="h-full overflow-hidden rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-[#1D305B]">Wallet · Meridian</span>
        <span className="rounded-full bg-[#0EA5A4]/10 px-2 py-0.5 text-[9px] font-semibold text-[#0EA5A4]">Multi-currency</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {[
          ["USD", "124,860.20"],
          ["EUR", "58,430.10"],
          ["INR", "84,12,400"],
        ].map(([c, v]) => (
          <div key={c} className="flex items-center justify-between rounded-lg bg-[#F8FAFC] px-3 py-2">
            <span className="font-mono-brand text-[10px] font-semibold text-[#64748B]">{c}</span>
            <span className="font-mono-brand text-[11.5px] font-semibold text-[#1D305B]">{v}</span>
          </div>
        ))}
      </div>
      <div className="mt-2.5 rounded-lg bg-[#1D305B] p-3">
        <p className="font-mono-brand text-[10px] tracking-[0.14em] text-white/80">5312 •••• •••• 4412</p>
        <p className="mt-1 text-[9px] text-white/40">Virtual card · issued from wallet balance</p>
      </div>
    </div>
  );
}

const MINI_VISUALS = { zenpay: <MiniZen />, emulation: <MiniEmulation />, wallet: <MiniWallet /> };

export function PortfolioSection() {
  return (
    <section data-testid="platform-portfolio" className="border-y border-[#E2E8F0] bg-white py-20 lg:py-28">
      <Container>
        <SectionHead eyebrow="Our Platforms" title="Modular platforms for the financial ecosystem." />
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {PLATFORMS.map((p, i) => (
            <Reveal
              key={p.id}
              delay={i * 100}
              data-testid={`platform-card-${p.id}`}
              className="group flex flex-col rounded-2xl border border-[#E2E8F0] bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-20px_rgba(15,23,42,0.18)]"
            >
              <div className="h-[228px] rounded-xl bg-[#F8FAFC] p-3">{MINI_VISUALS[p.id]}</div>
              <h3 className="mt-6 text-[22px] font-semibold text-[#1D305B]">{p.name}</h3>
              <p className="mt-1 text-[13px] font-semibold uppercase tracking-[0.12em] text-[#2563EB]">{p.subtitle}</p>
              <p className="mt-3.5 text-[15px] leading-relaxed text-[#64748B]">{p.desc}</p>
              <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-2.5">
                {p.capabilities.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-[#0EA5A4]" strokeWidth={3} aria-hidden="true" />
                    <span className="text-[13px] leading-snug text-[#475569]">{c}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={p.to}
                data-testid={`platform-card-${p.id}-cta`}
                className="group/link mt-auto inline-flex items-center gap-2 pt-7 text-[15px] font-semibold text-[#1D305B] transition-colors hover:text-[#2563EB]"
              >
                {p.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------- Section 14: ZenPay feature ---------- */

const RAIL_CHIPS = [
  { icon: CreditCard, label: "Cards", pos: "left-[-4%] top-[8%]", anim: "animate-float" },
  { icon: Landmark, label: "Bank Transfer", pos: "left-[-7%] top-[46%]", anim: "animate-float-2" },
  { icon: Wallet, label: "Wallets", pos: "left-[-3%] bottom-[10%]", anim: "animate-float-3" },
  { icon: Shuffle, label: "Alternative Payment Methods", pos: "right-[-6%] top-[12%]", anim: "animate-float-2" },
  { icon: Globe, label: "Cross-Border", pos: "right-[-3%] top-[50%]", anim: "animate-float" },
  { icon: Coins, label: "Multi-Currency", pos: "right-[-5%] bottom-[8%]", anim: "animate-float-3" },
];

export function ZenPayFeature() {
  return (
    <section data-testid="zenpay-feature" className="border-b border-[#E2E8F0] bg-[#F1F5F9] py-20 lg:py-28">
      <Container>
        <SectionHead
          center
          eyebrow="ZenPay"
          title="Neo-banking, with payments built in."
          copy="ZenPay is iJeeva's PCI-certified, multi-tenant neo-banking platform with a configurable white-label payment gateway at its core."
        />
        <Reveal delay={150} className="relative mx-auto mt-16 max-w-4xl">
          <DashboardMock />
          {RAIL_CHIPS.map((chip) => (
            <span
              key={chip.label}
              className={`absolute ${chip.pos} ${chip.anim} hidden items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#1D305B] shadow-[0_10px_30px_-12px_rgba(15,23,42,0.25)] xl:inline-flex`}
            >
              <chip.icon className="h-4 w-4 text-[#2563EB]" aria-hidden="true" />
              {chip.label}
            </span>
          ))}
        </Reveal>
        <div className="mt-8 flex flex-wrap justify-center gap-2.5 xl:hidden">
          {RAIL_CHIPS.map((chip) => (
            <span
              key={chip.label}
              className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-[13px] font-semibold text-[#1D305B]"
            >
              <chip.icon className="h-4 w-4 text-[#2563EB]" aria-hidden="true" />
              {chip.label}
            </span>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-[#94A3B8]">Dashboard shown with representative demo data.</p>
        <Reveal className="mt-10 text-center">
          <Link
            to="/platforms/zenpay"
            data-testid="zenpay-feature-cta"
            className="btn-brand group inline-flex items-center gap-2.5 rounded-[10px] px-6 py-3.5 text-[15px] font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-12px_rgba(37,99,235,0.55)]"
          >
            Discover ZenPay
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </Reveal>
      </Container>
    </section>
  );
}
